import { useMemo } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatPercent } from "@/lib/format";

interface SampleDonutProps {
  antisemitic: number;
  notAntisemitic: number;
  unclassified: number;
}

export function SampleDonut({
  antisemitic,
  notAntisemitic,
  unclassified,
}: SampleDonutProps) {
  const total = antisemitic + notAntisemitic + unclassified;

  const data = useMemo(
    () => [
      {
        name: "Antisemitic",
        value: antisemitic,
        color: "var(--color-destructive)",
      },
      {
        name: "Not antisemitic",
        value: notAntisemitic,
        color: "var(--color-success)",
      },
      ...(unclassified > 0
        ? [
            {
              name: "Unclassified",
              value: unclassified,
              color: "var(--color-muted-foreground)",
            },
          ]
        : []),
    ],
    [antisemitic, notAntisemitic, unclassified]
  );

  const rate = total > 0 ? antisemitic / total : 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Sample composition</CardTitle>
      </CardHeader>
      <CardContent className="flex items-center gap-6 pt-2">
        <div className="relative h-36 w-36">
          {total === 0 ? (
            <div className="flex h-full w-full items-center justify-center rounded-full border border-dashed border-border/60 text-xs text-muted-foreground">
              No data
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    dataKey="value"
                    innerRadius={48}
                    outerRadius={68}
                    stroke="var(--color-background)"
                    strokeWidth={2}
                    paddingAngle={1}
                  >
                    {data.map((slice) => (
                      <Cell key={slice.name} fill={slice.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-semibold tabular-nums">
                  {formatPercent(rate, 1)}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  antisemitic
                </span>
              </div>
            </>
          )}
        </div>
        <ul className="flex-1 space-y-2 text-sm">
          {data.map((slice) => (
            <li
              key={slice.name}
              className="flex items-center justify-between gap-3"
            >
              <span className="flex items-center gap-2 text-muted-foreground">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: slice.color }}
                />
                {slice.name}
              </span>
              <span className="tabular-nums text-foreground">
                {slice.value}
              </span>
            </li>
          ))}
          <li className="flex items-center justify-between gap-3 border-t border-border/40 pt-2 text-xs text-muted-foreground">
            <span>Total</span>
            <span className="tabular-nums">{total}</span>
          </li>
        </ul>
      </CardContent>
    </Card>
  );
}
